import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Search, FileText, Building2, Sparkles, MessageCircle, Linkedin, Instagram, ChevronRight, User, Share2, Users } from 'lucide-react';
import { WA_URL } from '../lib/utils';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_talent-match-304/artifacts/w2pnjd4z_4F63405D-91D9-42AD-A592-65AF89FFF01A.png';

const SectionLabel = ({ Icon, children }) => (
  <div className="flex items-center gap-2 mt-9 mb-3 px-1">
    <span className="w-8 h-8 rounded-xl bg-white/25 text-white flex items-center justify-center shrink-0 backdrop-blur-sm"><Icon className="w-4 h-4" /></span>
    <h2 className="text-xs font-extrabold uppercase tracking-[0.18em] text-white/85" style={{ fontFamily: 'Montserrat, sans-serif' }}>{children}</h2>
  </div>
);

const HubLink = ({ to, href, Icon, title, subtitle, testId, delay = 0 }) => {
  const content = (
    <>
      <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#FF8C19] to-[#E55F00] text-white flex items-center justify-center shrink-0 shadow-lg shadow-orange-900/30">
        <Icon className="w-5 h-5" />
      </span>
      <span className="flex-1 min-w-0 text-left">
        <span className="block font-bold text-slate-900 leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>{title}</span>
        {subtitle && <span className="block text-xs text-slate-500 mt-0.5">{subtitle}</span>}
      </span>
      <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-[#FF6B00] group-hover:translate-x-1 transition-[color,transform] duration-300 shrink-0" />
    </>
  );
  const cls = 'hub-item group flex items-center gap-4 w-full rounded-2xl px-4 py-4 bg-white hover:bg-white border border-white/60 hover:border-[#FF6B00]/50 shadow-lg shadow-orange-950/10 hover:shadow-xl hover:shadow-orange-950/15 transition-[box-shadow,border-color,transform] duration-300 hover:-translate-y-0.5';
  const style = { animationDelay: `${delay}ms` };
  if (to) return <Link to={to} data-testid={testId} className={cls} style={style}>{content}</Link>;
  return <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId} className={cls} style={style}>{content}</a>;
};

const AcessarPage = () => {
  useEffect(() => {
    const prev = document.title;
    document.title = 'HM Talent — Central de Acesso';
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="relative min-h-screen w-full overflow-hidden flex justify-center" style={{ background: 'linear-gradient(165deg, #FFB255 0%, #FF7A1A 45%, #EA5F00 100%)' }} data-testid="acessar-page">
      <style>{`
        @keyframes hubFadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .hub-item, .hub-fade { opacity: 0; animation: hubFadeUp 0.6s cubic-bezier(0.22,1,0.36,1) forwards; }
      `}</style>
      {/* glows */}
      <div className="pointer-events-none absolute -top-40 -right-24 w-[32rem] h-[32rem] rounded-full opacity-50 blur-3xl" style={{ background: 'radial-gradient(circle, #FFD9A0 0%, transparent 70%)' }} />
      <div className="pointer-events-none absolute -bottom-24 -left-32 w-[30rem] h-[30rem] rounded-full opacity-40 blur-3xl" style={{ background: 'radial-gradient(circle, #C24F00 0%, transparent 70%)' }} />
      <div className="pointer-events-none absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '28px 28px' }} />

      <div className="relative w-full max-w-md px-5 py-14 sm:py-16">
        {/* Header */}
        <div className="flex flex-col items-center text-center hub-fade" style={{ animationDelay: '0ms' }}>
          <div className="bg-white rounded-3xl p-4 shadow-2xl shadow-orange-900/40">
            <img src={LOGO_URL} alt="HM Talent" className="h-16 w-auto" />
          </div>
          <h1 className="mt-8 text-2xl sm:text-3xl font-black text-white leading-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Como podemos ajudar<br /><span className="text-[#4A1B00]">você hoje?</span>
          </h1>
          <p className="mt-3 text-sm text-white/85">Escolha por onde quer começar.</p>
        </div>

        {/* Candidato */}
        <SectionLabel Icon={User}>Sou Candidato</SectionLabel>
        <div className="space-y-3">
          <HubLink to="/para-candidatos" Icon={Search} title="Ver vagas em aberto" subtitle="Oportunidades atualizadas" testId="acessar-vagas" delay={80} />
          <HubLink to="/portal/candidato/cadastro" Icon={FileText} title="Cadastrar currículo" subtitle="Entre no nosso banco de talentos" testId="acessar-curriculo" delay={140} />
          <HubLink href="https://chat.whatsapp.com/KwHinFwPNlaB8UjEvhZPeW?s=cl&p=i&mlu=0&ilr=0" Icon={Users} title="Grupo de vagas no WhatsApp" subtitle="Receba as oportunidades em primeira mão" testId="acessar-grupo-vagas" delay={200} />
        </div>

        {/* Empresa */}
        <SectionLabel Icon={Building2}>Sou Empresa</SectionLabel>
        <div className="space-y-3">
          <HubLink to="/portal/empresa/login" Icon={Building2} title="Acessar Portal da Empresa" subtitle="Acompanhe seus processos" testId="acessar-portal-empresa" delay={200} />
          <HubLink to="/para-empresas" Icon={Sparkles} title="Conheça nossas soluções" subtitle="Recrutamento ágil e assertivo" testId="acessar-solucoes" delay={260} />
        </div>

        {/* Contato */}
        <SectionLabel Icon={MessageCircle}>Falar com a HM Talent</SectionLabel>
        <div className="space-y-3">
          <HubLink href={WA_URL('Olá! Vim pela Central de Acesso da HM Talent e gostaria de conversar.')} Icon={MessageCircle} title="WhatsApp" subtitle="Resposta rápida no horário comercial" testId="acessar-whatsapp" delay={320} />
        </div>

        {/* Redes sociais */}
        <SectionLabel Icon={Share2}>Redes Sociais</SectionLabel>
        <div className="space-y-3">
          <HubLink href="https://www.linkedin.com/company/hmtalentrh" Icon={Linkedin} title="LinkedIn HM Talent" subtitle="Novidades e vagas da empresa" testId="acessar-linkedin-hm" delay={380} />
          <HubLink href="https://www.linkedin.com/in/higormonteiroh/" Icon={Linkedin} title="LinkedIn do Higor" subtitle="Conecte-se com o fundador" testId="acessar-linkedin-higor" delay={440} />
          <HubLink href="https://instagram.com/hmtalentrh" Icon={Instagram} title="Instagram" subtitle="@hmtalentrh" testId="acessar-instagram" delay={500} />
        </div>

        {/* CTA rodapé */}
        <div className="hub-fade mt-12 text-center" style={{ animationDelay: '560ms' }}>
          <div className="mx-auto w-16 h-px bg-gradient-to-r from-transparent via-white/70 to-transparent mb-5" />
          <p className="text-sm font-semibold text-white/90 leading-relaxed" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Uma plataforma completa para conectar<br className="hidden sm:block" /> empresas e talentos.
          </p>
          <p className="mt-4 text-xs text-white/60">© {new Date().getFullYear()} HM Talent</p>
        </div>
      </div>
    </div>
  );
};

export default AcessarPage;
